import clsx from 'clsx';
import { ChevronUp, ChevronDown, Eye, Edit, Trash, RefreshCw } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Card } from '@components/ui';
import Pagination from '@components/ui/Pagination';
import { formatCurrency, formatDate, getDurumBadge } from '../../components/StockIndicator';
import { calculateKDV } from '@utils/kdv';

const ITEMS_PER_PAGE = 10;

export default function BetonTable({ data, onView, onEdit, onDelete, loading }) {
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);

    const handleSort = (key) => {
        if (sortKey === key) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortKey(key); setSortDirection('asc'); }
        setCurrentPage(1);
    };

    const sortedData = useMemo(() => {
        const sorted = [...data].sort((a, b) => {
            if (!sortKey) return 0;
            const aVal = a[sortKey], bVal = b[sortKey];
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [data, sortKey, sortDirection]);

    const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedData.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedData, currentPage]);

    if (loading) return <Card padding="lg" className="text-center"><RefreshCw size={24} className="animate-spin mx-auto text-primary" /><p className="text-text-muted mt-3">Yükleniyor...</p></Card>;
    if (data.length === 0) return <Card padding="lg" className="text-center"><p className="text-text-muted">Henüz beton siparişi bulunmuyor</p></Card>;

    const SortIcon = ({ colKey }) => { if (sortKey !== colKey) return null; return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />; };

    const getKDV = (row) => {
        if (row.kdvDahilToplam) return { toplamHaric: row.toplamFiyat, kdvOrani: row.kdvOrani || 20, toplamDahil: row.kdvDahilToplam };
        return calculateKDV({ unitPrice: row.birimFiyat, quantity: row.miktar, vatRate: row.kdvOrani });
    };

    const thClass = "py-3 px-4 text-sm font-semibold text-text-primary";
    const thSortClass = `${thClass} cursor-pointer hover:bg-bg-hover`;
    const rowNumberOffset = (currentPage - 1) * ITEMS_PER_PAGE;

    return (
        <>
            {/* Desktop Table */}
            <Card padding="none" className="hidden md:block overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-bg-tertiary border-b border-border-light">
                                <th className={thClass}>#</th>
                                <th onClick={() => handleSort('siparisTarihi')} className={thSortClass}><div className="flex items-center gap-1">Tarih <SortIcon colKey="siparisTarihi" /></div></th>
                                <th onClick={() => handleSort('tedarikci')} className={thSortClass}><div className="flex items-center gap-1">Tedarikçi <SortIcon colKey="tedarikci" /></div></th>
                                <th className={thClass}>Sınıf</th>
                                <th onClick={() => handleSort('miktar')} className={thSortClass}><div className="flex items-center gap-1">Miktar <SortIcon colKey="miktar" /></div></th>
                                <th className={thClass}>Döküm Yeri</th>
                                <th onClick={() => handleSort('toplamFiyat')} className={`text-right ${thSortClass}`}><div className="flex items-center justify-end gap-1">KDV Hariç <SortIcon colKey="toplamFiyat" /></div></th>
                                <th className={`${thClass} text-center`}>KDV %</th>
                                <th className={`${thClass} text-right`}>KDV Dahil</th>
                                <th className={thClass}>Durum</th>
                                <th className={`${thClass} text-center w-28`}>İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, idx) => {
                                const kdv = getKDV(row);
                                return (
                                    <tr key={row.id} className={clsx("border-b border-border-light hover:bg-bg-hover transition-colors", idx % 2 === 0 ? "bg-bg-secondary" : "bg-bg-primary")}>
                                        <td className="py-3 px-4 text-sm text-text-muted font-medium text-center tabular-nums">{rowNumberOffset + idx + 1}</td>
                                        <td className="py-3 px-4 text-sm text-text-secondary">{formatDate(row.siparisTarihi)}</td>
                                        <td className="py-3 px-4 text-sm text-text-primary">{row.tedarikci}</td>
                                        <td className="py-3 px-4"><span className="px-2 py-1 bg-bg-tertiary rounded text-xs font-medium text-text-primary">{row.sinif}</span></td>
                                        <td className="py-3 px-4 text-sm text-text-primary font-medium">{row.miktar} {row.birim}</td>
                                        <td className="py-3 px-4 text-sm text-text-secondary">{row.dokumYeri}</td>
                                        <td className="py-3 px-4 text-sm text-text-primary text-right">{formatCurrency(kdv.toplamHaric)}</td>
                                        <td className="py-3 px-4 text-sm text-text-muted text-center">%{kdv.kdvOrani}</td>
                                        <td className="py-3 px-4 text-sm font-semibold text-success text-right">{formatCurrency(kdv.toplamDahil)}</td>
                                        <td className="py-3 px-4">{getDurumBadge(row.durum)}</td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-center gap-1">
                                                <button onClick={() => onView(row)} className="p-1.5 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-info transition-colors" title="Görüntüle"><Eye size={16} /></button>
                                                <button onClick={() => onEdit(row)} className="p-1.5 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-primary transition-colors" title="Düzenle"><Edit size={16} /></button>
                                                <button onClick={() => onDelete(row)} className="p-1.5 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-error transition-colors" title="Sil"><Trash size={16} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 border-t border-border-light bg-bg-tertiary">
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={sortedData.length} pageSize={ITEMS_PER_PAGE} />
                </div>
            </Card>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {paginatedData.map((row) => {
                    const kdv = getKDV(row);
                    return (
                        <Card key={row.id} padding="md">
                            <div className="flex justify-between items-start mb-2">
                                <div><p className="font-medium text-text-primary">{row.tedarikci}</p><p className="text-sm text-text-muted">{formatDate(row.siparisTarihi)}</p></div>
                                {getDurumBadge(row.durum)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-text-secondary mb-2">
                                <span className="px-2 py-0.5 bg-bg-tertiary rounded font-medium">{row.sinif}</span>
                                <span>{row.miktar} {row.birim}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm mb-3">
                                <span className="text-text-secondary">KDV Hariç: <span className="text-text-primary font-medium">{formatCurrency(kdv.toplamHaric)}</span></span>
                                <span className="text-success font-semibold">KDV Dahil: {formatCurrency(kdv.toplamDahil)}</span>
                            </div>
                            <div className="flex items-center gap-2 border-t border-border-light pt-3">
                                <button onClick={() => onView(row)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-bg-tertiary text-text-secondary text-sm hover:bg-bg-hover"><Eye size={14} /> Görüntüle</button>
                                <button onClick={() => onEdit(row)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-bg-tertiary text-text-secondary text-sm hover:bg-bg-hover"><Edit size={14} /> Düzenle</button>
                                <button onClick={() => onDelete(row)} className="p-2 rounded-lg bg-bg-tertiary text-error hover:bg-error/10"><Trash size={14} /></button>
                            </div>
                        </Card>
                    );
                })}
                {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={sortedData.length} pageSize={ITEMS_PER_PAGE} />}
            </div>
        </>
    );
}
